<script lang="ts">
import { defineComponent, PropType } from 'vue'
import ButtonClose from '@/components/button/ButtonClose.vue'

export default defineComponent({
  name: 'ToastItem',

  components: {
    ButtonClose,
  },

  props: {
    messageType: {
      type: String as PropType<MessageType>,
      default: 'notice',
    },

    /**
     * 本文の最大表示行数
     * @description 0 のとき無制限（全行表示）。
     */
    maxLine: {
      type: Number,
      default: 2,
    },
  },

  emits: ['close'],
})
</script>

<template>
  <div
    class="l-unit__toastItem"
    :data-type="messageType">
    <div class="l-unit__toastItem--description">
      <p
        class="p"
        :style="{ '-webkit-line-clamp': maxLine > 0 ? maxLine : 'none' }">
        <slot />
      </p>
    <!-- /.l-unit__toastItem description --></div>

    <div class="l-unit__toastItem--action is-close">
      <ButtonClose
        color="darkGray"
        :has-tooltip="false"
        prevent
        @click:close="$emit('close')" />
    <!-- /.l-unit__toastItem action --></div>
  <!-- /.l-unit__toastItem --></div>
</template>

<style lang="sass" scoped>
// layout
.l-unit__toastItem
  $root: &
  $close-button-size: 26px
  $close-button-margin: 20px
  flex-grow: 1
  position: relative

  & &--description
    $icon-size-px: 20
    $icon-size-em: math.div($icon-size-px, $font-normal-px)
    height: 100%
    min-height: changeSizePx2Em($toast-item-height)
    display: flex
    align-items: center
    // white-space: nowrap  // css アニメーションで高さを固定値で使う必要があるため、改行を禁止
    // ↓ 上下 padding を変えると、グローバル Toast の固定高 $toast-item-height に本文が収まらず見切れる恐れ。変更時は $toast-item-height を再検算すること。
    padding:
      top: 0.5em
      bottom: 0.5em
      left: 1em
      right: $close-button-size + $close-button-margin * 2
    border-radius: $border-radius
    box-shadow: $drop-bottom-shadow

    +breakpoint
      font-size: $font-small
      min-height: changeSizePx2Em($toast-item-height, $font-small-px)

    &::before
      content: ""
      width: #{$icon-size-em}em
      height: #{$icon-size-em}em
      display: block
      margin-right: 1em
      background-repeat: no-repeat

    > .p
      flex: 1
      line-height: 1.4
      // 行数クランプは maxLine（インラインで -webkit-line-clamp を付与。0 のとき無制限）
      display: -webkit-box
      -webkit-box-orient: vertical
      overflow: hidden

  & &--action
    &.is-close
      width: $close-button-size
      height: $close-button-size
      position: absolute
      top: 50%
      right: 20px
      transform: translateY(-50%)

  &[data-type="notice"]
    #{$root}--description
      background-color: rgba($bg-notice, 1)
      border: 1px solid $border-notice

      &::before
        background-image: url(@/images/icon/toast/icon-notice.svg)

  &[data-type="success"]
    #{$root}--description
      background-color: rgba($bg-success, 1)
      border: 1px solid $border-success

      &::before
        background-image: url(@/images/icon/toast/icon-success.svg)

  &[data-type="warning"]
    #{$root}--description
      background-color: rgba($bg-warning, 1)
      border: 1px solid $border-warning

      &::before
        background-image: url(@/images/icon/toast/icon-warning.svg)

  &[data-type="alert"]
    #{$root}--description
      background-color: rgba($bg-alert, 1)
      border: 1px solid $border-alert

      &::before
        background-image: url(@/images/icon/toast/icon-alert.svg)
</style>
